import React from "react"
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons"
import { Alert, Col, Form, Input, notification, Row, Select, Spin } from "antd"
import api from "src/api"
import { Button } from "src/components/actions/button"
import { SaveSvg } from "src/components/Svg"
import styled from "styled-components"

export const FEED_URL_TYPES = [
  { label: "Сайт (url)", value: "main" },
  { label: "Соцсеть (add-url)", value: "add" },
]

/**
 * Ссылки для внешних фидов: сайт филиала и соцсети.
 *
 * Компонент работает и с точкой, и с типом точки — различается только адрес
 * ресурса. Набор сохраняется целиком: порядок строк задаёт порядок тегов
 * в фиде, пустой список означает откат на уровень выше (тип точки, затем
 * настройки сети).
 */
export function FeedUrlsEditor({ endpoint, platform, hint }) {
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)

  React.useEffect(() => {
    if (endpoint) {
      loadUrls()
    }
  }, [endpoint, platform])

  async function loadUrls() {
    setIsLoading(true)
    try {
      const response = await api.get(`${endpoint}?platform=${platform}`)
      form.setFieldsValue({ urls: response.data.items })
    } catch (error) {
      console.error("ошибка загрузки ссылок", error)
      notification.error({ message: "Не удалось загрузить ссылки" })
    } finally {
      setIsLoading(false)
    }
  }

  async function onSave() {
    setIsSaving(true)
    try {
      const values = await form.validateFields()
      const urls = (values.urls ?? []).filter((item) => item?.url)

      await api.put(endpoint, { platform, urls })
      notification.success({ message: "Ссылки сохранены" })
    } catch (error) {
      console.error("ошибка сохранения ссылок", error)
      notification.error({ message: "Ошибка сохранения ссылок" })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Spin spinning={isLoading}>
      <Form form={form} layout="vertical">
        {hint && <Alert type="info" showIcon style={{ marginBottom: 16 }} message={hint} />}
        <Form.List name="urls">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ name, key, ...field }) => (
                <Row gutter={16} align="middle" key={key}>
                  <Col span={7}>
                    <Form.Item
                      {...field}
                      name={[name, "type"]}
                      label="Тип"
                      rules={[{ required: true, message: "Выберите тип" }]}
                    >
                      <Select options={FEED_URL_TYPES} />
                    </Form.Item>
                  </Col>
                  <Col span={15}>
                    <Form.Item
                      {...field}
                      name={[name, "url"]}
                      label="Ссылка"
                      rules={[{ required: true, message: "Укажите ссылку" }]}
                    >
                      <Input placeholder="https://vk.ru/bushe.bakery" />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Col>
                </Row>
              ))}
              <Button type="dashed" onClick={() => add({ type: "add", url: "" })} block>
                <PlusOutlined /> Добавить ссылку
              </Button>
            </>
          )}
        </Form.List>
      </Form>
      <StyledFooter>
        <Button type="primary" loading={isSaving} onClick={onSave}>
          <SaveSvg /> Сохранить ссылки
        </Button>
      </StyledFooter>
    </Spin>
  )
}

const StyledFooter = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
`
